import { useMutation, useQuery } from '@apollo/client'
import {
  Button,
  Hidden,
  Typography,
  useMediaQuery,
  useTheme
} from '@material-ui/core'
import { useEffect, useMemo, useState } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'

import noResultsFound from '../../assets/images/noResultFound.svg'
import { totalQuantityVar } from '../../cache/cart'
import AdotelSkeleton from '../../components/AdotelSkeleton'
import PromotionSummary from '../../components/PromotionSummary'
import { RedirectModal } from '../../components/RedirectModal'
import { GET_CART_QUERY } from '../../graphql/cartCrud'
import {
  CONFIRM_PRIMITIVE_RESERVATION,
  MAKE_PRIMITIVE_RESERVATION
} from '../../graphql/cartPrimitive'
import { cartItemPublicIdentity } from '../../helpers/cartIdentity'
import { useWindowDimensions } from '../../helpers/pageSize'
import {
  buildPromotionIntent,
  classifyPrimitiveFailure,
  PromotionErrorCode,
  promotionErrorMessage
} from '../../helpers/promotion'
import { errorNoCartFound, extractError } from '../../helpers/regex'
import { cleanCartData } from '../../helpers/userData'
import { getTranslate } from '../../localization'
import {
  CartResponse,
  ConfirmPrimitiveReservationInput,
  ConfirmPrimitiveReservation,
  MakePrimitiveReservation,
  BillingSummary,
  CartPaymentInfo,
  GrpcInput,
  AccommodationInfo,
  PaymentMethod,
  PageStepper
} from '../../types/cart'
import StepOne from './StepOne'
import StepThree from './StepThree'
import StepTwo from './StepTwo'
import { buildConfirmPrimitiveInput } from './buildConfirmPrimitiveInput'
import CustomStepper from './components/CustomStepper'
import ExpireTimeCounter from './components/ExpireTimeCounter'
import FinalBillingSummary from './components/FinalBillingSummary'
import HotelCard from './components/HotelCard'
import PaymentGateWay from './components/PaymentGateWay'
import PromotionCodeEntry from './components/PromotionCodeEntry'
import { isContinueDisabled } from './isContinueDisabled'
import {
  isStoredConfirmation,
  isStoredConfirmInput,
  isStoredPrimitive,
  paymentCartID,
  readCartState,
  writeCartState
} from './storedBookingState'
import useStyles from './styles/index.styles'
import { validateConfirmPrimitiveInput } from './validateConfirmPrimitiveInput'

const BookingStepper = () => {
  const classes = useStyles()
  const theme = useTheme()
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'))
  const params = useParams<{ page: PageStepper }>()
  const [cartID] = useState(
    () =>
      localStorage.getItem('cartID') ||
      (params.page === 'payment' ? paymentCartID() : '')
  )
  const [isTimeExpired, setIsTimeExpired] = useState(false)
  const [showModal, setShowModal] = useState(false)
  const redirect = localStorage.getItem('Referer')
  const [redirectTitle, setRedirectTitle] = useState('')
  const [clickedItemHash, setClickedItemHash] = useState<string>('hash')
  const [paymentMethod] = useState<PaymentMethod>('Online')
  const [expireTime, setExpireTime] = useState('')
  // Code entry stays local until Continue: MakePrimitiveReservation creates a
  // real booking hold, so it must never be used as a debounced validation call.
  const [promotionCode, setPromotionCode] = useState('')
  const [promotionErrorCode, setPromotionErrorCode] =
    useState<PromotionErrorCode>()
  const history = useHistory()
  const { width } = useWindowDimensions()
  const [hotelDetails, setHotelDetails] = useState<{
    accommodationInfo: AccommodationInfo
    walletType: string
    provider: string
  }>()

  const [billingData, setBillingData] = useState<BillingSummary>({
    roomsPrice: [],
    subTotalPrice: {
      subTotalBeforeDiscount: 0,
      discount: 0,
      additionalDiscount: 0,
      subTotalAfterDiscount: 0
    },
    taxAndFees: 0,
    totalPayable: 0,
    curruncy: 'IRR'
  })
  const [paymentInfo, setPaymentInfo] = useState<CartPaymentInfo>({
    walletID: 0,
    walletType: '',
    cashBalance: 0,
    remainingCreditBalance: 0,
    isBilateral: false,
    currency: 'IRR',
    creditorID: 0
  })

  const [grpcInput, setGrpcInput] = useState<GrpcInput>({
    channelCode: '',
    propertyCode: '',
    providerCode: ''
  })

  const [confirmedState, setConfirmedState] = useState<{
    cartID: string
    data: ConfirmPrimitiveReservation
  }>()
  const confirmPrimitiveReservationState =
    confirmedState?.cartID === cartID ? confirmedState.data : undefined

  const [confirmPrimitiveInput, setConfirmPrimitiveInput] =
    useState<ConfirmPrimitiveReservationInput>({
      reservationNumber: 0,
      pmsRequestedMethod: 'Sync',
      booker: {
        firstName: '',
        lastName: '',
        email: '',
        identificationNo: '',
        mobile: '',
        isForeigner: false
      },
      discountPercent: 0,
      confirmRooms: [],
      walletID: 0,
      grpcInput: { channelCode: '', propertyCode: '', providerCode: '' },
      cartID: cartID
    })

  const [primitiveState, setPrimitiveState] = useState<{
    cartID: string
    data: MakePrimitiveReservation
  }>()
  const primitiveData =
    primitiveState?.cartID === cartID ? primitiveState.data : undefined

  useEffect(() => {
    const savedPrimitive = readCartState(
      'primitiveData',
      cartID,
      isStoredPrimitive
    )
    const savedInput = readCartState(
      'confirmPrimitiveInput',
      cartID,
      isStoredConfirmInput
    )
    const savedConfirmation = readCartState(
      'confirmPrimitiveReservationState',
      cartID,
      isStoredConfirmation
    )
    const hotelDetailsLocalStorage = localStorage.getItem('hotelDetails')

    if (
      savedPrimitive &&
      savedInput &&
      savedInput.reservationNumber === savedPrimitive.reservationNumber
    ) {
      setConfirmPrimitiveInput(savedInput)
    }

    if (savedPrimitive) {
      setPrimitiveState({ cartID, data: savedPrimitive })
    }

    if (savedConfirmation) {
      setConfirmedState({ cartID, data: savedConfirmation })
    }

    if (hotelDetailsLocalStorage) {
      try {
        setHotelDetails(JSON.parse(hotelDetailsLocalStorage))
      } catch {
        localStorage.removeItem('hotelDetails')
      }
    }
  }, [cartID])

  //GET_CART_QUERY
  const {
    data: cartData,
    loading: loadingForGetCart,
    refetch: refetchCart
  } = useQuery<{
    cartResponse: CartResponse
  }>(GET_CART_QUERY, {
    notifyOnNetworkStatusChange: true,
    skip: !cartID || params.page === 'payment',
    fetchPolicy: 'network-only',
    variables: { cartID: cartID },
    onCompleted: (res) => {
      if (res.cartResponse.cart.items.length === 0) {
        setRedirectTitle('Cart is empty')
        setShowModal(true)
      } else {
        setHotelDetails({
          accommodationInfo: res.cartResponse.cart.accommodationInfo,
          walletType: res.cartResponse.cart.paymentInfo.walletType,
          provider: cartItemPublicIdentity(res.cartResponse.cart.items[0]).name
        })

        const elemens = res.cartResponse.cart.items || []
        const totalQuantity = elemens.reduce(
          (sum, item) => sum + (item.quantity || 0),
          0
        )
        totalQuantityVar(totalQuantity)
        localStorage.setItem('totalQuantity', totalQuantity.toString())
      }
    },
    onError: (err) => {
      if (extractError(err.cause?.extensions) === errorNoCartFound) {
        setShowModal(true)
        setRedirectTitle('Your cart has expired.')
        cleanCartData()
        localStorage.removeItem('confirmPrimitiveInput')
        localStorage.removeItem('confirmPrimitiveReservationState')
        localStorage.removeItem('hotelDetails')
        localStorage.removeItem('primitiveData')
      } else {
        toast.error(getTranslate('Something went wrong'))
      }
    }
  })

  const cartKey = useMemo(
    () => JSON.stringify(cartData?.cartResponse.cart ?? null),
    [cartData?.cartResponse.cart]
  )

  //MAKE_PRIMITIVE_RESERVATION
  const [makePrimitive, { loading: loadingForMakePrimitive }] = useMutation<{
    makePrimitiveReservation: MakePrimitiveReservation
  }>(MAKE_PRIMITIVE_RESERVATION, {
    notifyOnNetworkStatusChange: true,
    onCompleted: (res) => {
      setPromotionErrorCode(undefined)
      setPrimitiveState({ cartID, data: res.makePrimitiveReservation })

      setConfirmPrimitiveInput((prev) =>
        buildConfirmPrimitiveInput(
          prev,
          res.makePrimitiveReservation,
          cartData?.cartResponse.cart.channelCode,
          cartID
        )
      )

      history.replace('/bookstepper/form')
    },
    onError: (err) => {
      const failure = classifyPrimitiveFailure(
        err,
        (e) =>
          extractError(
            (e as { cause?: { extensions?: Record<string, unknown> } }).cause
              ?.extensions
          ) === errorNoCartFound
      )
      if (failure.kind === 'transport') {
        setPromotionErrorCode(failure.code)
        toast.error(getTranslate(promotionErrorMessage(failure.code)))
        return
      }
      // A promotion rejected AT BOOKING TIME still needs words. A refusal is meant to
      // arrive from evaluatePromotion carrying the service's own translated sentence, but
      // the booker can reach here by typing a code and pressing Continue without pressing
      // Apply, since handleStepUp sends whatever is in the field. Falling through would
      // hand them toast.error(err.message): raw, English, server-worded.
      //
      // Generic on purpose. The specific reason belongs to Apply, and inventing a detailed
      // one here would rebuild the local table this change removed.
      if (failure.kind === 'promotion') {
        toast.error(
          getTranslate(
            'The promotion could not be applied. You can continue without it.'
          )
        )
        return
      }
      if (failure.kind === 'cartExpired') {
        setShowModal(true)
        setRedirectTitle('Your cart has expired.')
        cleanCartData()
        localStorage.removeItem('confirmPrimitiveInput')
        localStorage.removeItem('confirmPrimitiveReservationState')
        localStorage.removeItem('hotelDetails')
        localStorage.removeItem('primitiveData')
      } else {
        toast.error(err.message)
      }
    }
  })

  //CONFIRM_PRIMITIVE_RESERVATION
  const [
    confirmPrimitiveReservation,
    { loading: loadingForConfirmPrimitiveData }
  ] = useMutation<{ confirmPrimitiveReservation: ConfirmPrimitiveReservation }>(
    CONFIRM_PRIMITIVE_RESERVATION,
    {
      notifyOnNetworkStatusChange: true,
      onCompleted: (res) => {
        setConfirmedState({ cartID, data: res.confirmPrimitiveReservation })
        history.replace('/bookstepper/payment')
        cleanCartData()
      },
      onError: (err) => {
        toast.error(err.message)
      }
    }
  )

  useEffect(() => {
    if (primitiveData && cartData?.cartResponse.cart.channelCode) {
      setConfirmPrimitiveInput((prev) =>
        buildConfirmPrimitiveInput(
          prev,
          primitiveData,
          cartData.cartResponse.cart.channelCode,
          cartID
        )
      )
    }
  }, [primitiveData, cartData?.cartResponse.cart.channelCode, cartID])

  useEffect(() => {
    if (confirmPrimitiveInput.cartID === cartID) {
      writeCartState('confirmPrimitiveInput', cartID, confirmPrimitiveInput)
    }

    if (primitiveData) {
      writeCartState('primitiveData', cartID, primitiveData)
    }

    if (confirmPrimitiveReservationState) {
      writeCartState(
        'confirmPrimitiveReservationState',
        cartID,
        confirmPrimitiveReservationState
      )
    }

    if (hotelDetails) {
      localStorage.setItem('hotelDetails', JSON.stringify(hotelDetails))
    }
  }, [
    confirmPrimitiveInput,
    primitiveData,
    confirmPrimitiveReservationState,
    hotelDetails,
    cartID
  ])

  const renderStep = () => {
    switch (params.page) {
      case 'cart':
        return (
          <StepOne
            cart={cartData?.cartResponse.cart}
            cartID={cartID}
            refetchCart={refetchCart}
            loadingForGetCart={loadingForGetCart}
            setShowModal={setShowModal}
            clickedItemHash={clickedItemHash}
            setClickedItemHash={setClickedItemHash}
            setRedirectTitle={setRedirectTitle}
            setIsTimeExpired={setIsTimeExpired}
          />
        )
      case 'form':
        return (
          <StepTwo
            primitiveData={primitiveData}
            confirmPrimitiveInput={confirmPrimitiveInput}
            setConfirmPrimitiveInput={setConfirmPrimitiveInput}
            setShowModal={setShowModal}
            isTimeExpired={isTimeExpired}
            setIsTimeExpired={setIsTimeExpired}
            setRedirectTitle={setRedirectTitle}
          />
        )
      case 'payment':
        return (
          <StepThree
            confirmData={confirmPrimitiveReservationState}
            isTimeExpired={isTimeExpired}
            setShowModal={setShowModal}
            setRedirectTitle={setRedirectTitle}
          />
        )
      default:
        return
    }
  }

  const handleStepUp = () => {
    if (params.page === 'cart') {
      const promotionIntent = buildPromotionIntent(promotionCode)
      if (!promotionIntent) {
        setPromotionErrorCode('PROMOTION_CODE_TOO_LONG')
        return
      }
      makePrimitive({
        variables: {
          input: {
            cartID: cartID,
            grpcInput: grpcInput,
            promotionIntent
          }
        }
      })
    } else if (params.page === 'form') {
      if (!primitiveData) {
        toast.error(getTranslate('Something went wrong'))
        return
      }
      const input = buildConfirmPrimitiveInput(
        confirmPrimitiveInput,
        primitiveData,
        cartData?.cartResponse.cart.channelCode,
        cartID
      )
      if (!validateConfirmPrimitiveInput(input)) {
        toast.error(getTranslate('Something went wrong'))
        return
      }
      confirmPrimitiveReservation({
        variables: {
          input
        }
      })
    }
  }

  const handleStepDown = () => {
    if (params.page === 'form') {
      history.replace('/bookstepper/cart')
    } else {
      history.goBack()
    }
  }

  useEffect(() => {
    if (
      params.page === 'cart' &&
      cartData &&
      cartData.cartResponse.cart.items.length > 0
    ) {
      setBillingData(cartData.cartResponse.billingSummary)
      setPaymentInfo(cartData.cartResponse.cart.paymentInfo)
      setGrpcInput({
        channelCode: cartData.cartResponse.cart.channelCode,
        propertyCode: cartData.cartResponse.cart.accommodationInfo.propertyCode,
        providerCode: cartData.cartResponse.cart.items[0]
          ? cartData.cartResponse.cart.items[0].provider?.code ?? ''
          : ''
      })
    }
    if (params.page === 'form' && primitiveData) {
      setBillingData(primitiveData.billingSummary)
      setPaymentInfo(primitiveData.paymentInfo)
      setExpireTime(primitiveData?.expiresAt.replace(' ', 'T') || '')
    }
    if (params.page === 'payment' && confirmPrimitiveReservationState) {
      setBillingData(confirmPrimitiveReservationState.billingSummary)
      setPaymentInfo(confirmPrimitiveReservationState.paymentInfo)
      setExpireTime(
        confirmPrimitiveReservationState.reservationInfo.expiresAt.replace(
          ' ',
          'T'
        ) || ''
      )
    }
  }, [params.page, cartData, primitiveData, confirmPrimitiveReservationState])

  const calcStep = (p: string) => {
    switch (p) {
      case 'cart':
        return 0

      case 'form':
        return 1

      case 'payment':
        return 2

      default:
        return 0
    }
  }

  const promotionApplication =
    params.page === 'cart'
      ? cartData?.cartResponse.cart.promotionApplication
      : params.page === 'form'
      ? primitiveData?.promotionApplication
      : confirmPrimitiveReservationState?.reservationInfo.promotionApplication

  const ratePlanNames: Record<string, string> = {}
  if (params.page === 'cart') {
    cartData?.cartResponse.cart.items.forEach((item) => {
      ratePlanNames[item.ratePlan.code] = item.ratePlan.name
    })
  } else if (params.page === 'form') {
    primitiveData?.roomStays.forEach((stay) => {
      ratePlanNames[stay.ratePlanCode] = stay.ratePlanName
    })
  } else {
    confirmPrimitiveReservationState?.reservationInfo.detailedRoomStays.forEach(
      (stay) => {
        ratePlanNames[stay.ratePlanCode] = stay.ratePlanName
      }
    )
  }

  return (
    <>
      <div className={classes.stickyDiv}>
        <div className={classes.stepContainer}>
          <CustomStepper activeStep={calcStep(params.page)} />
        </div>
      </div>
      <div
        className={[
          classes.root,
          isSmall
            ? classes.rootSmall
            : width <= 1280
            ? classes.root1280
            : width < 1440
            ? classes.root1440
            : width <= 1680
            ? classes.root1680
            : ''
        ].join(' ')}>
        {cartData || primitiveData || confirmPrimitiveReservationState ? (
          <>
            <div className={classes.left}>
              <Hidden smUp>
                {params.page !== 'cart' && (
                  <ExpireTimeCounter
                    expiryTime={expireTime}
                    setIsTimeExpired={setIsTimeExpired}
                  />
                )}
              </Hidden>
              <HotelCard hotelDetails={hotelDetails} />
              {renderStep()}
            </div>
            <div className={classes.Right}>
              {params.page !== 'cart' && (
                <Hidden smDown>
                  <ExpireTimeCounter
                    expiryTime={expireTime}
                    setIsTimeExpired={setIsTimeExpired}
                  />
                </Hidden>
              )}
              <FinalBillingSummary
                billingData={billingData}
                step={calcStep(params.page) + 1}
                ratePlanNames={ratePlanNames}
              />
              {params.page === 'cart' && (
                <PromotionCodeEntry
                  cartID={cartID}
                  cartKey={cartKey}
                  cartItems={cartData?.cartResponse.cart.items}
                  code={promotionCode}
                  disabled={loadingForMakePrimitive}
                  errorCode={promotionErrorCode}
                  onChange={(code) => {
                    setPromotionCode(code)
                    setPromotionErrorCode(undefined)
                  }}
                />
              )}
              <PromotionSummary application={promotionApplication} />
              {params.page === 'payment' && !isSmall && (
                <PaymentGateWay
                  totalAmount={
                    confirmPrimitiveReservationState?.reservationInfo
                      .totalAmount
                  }
                  paymentInfo={paymentInfo}
                  paymentMethod={paymentMethod}
                  reservationNumber={
                    confirmPrimitiveReservationState?.reservationNumber || 0
                  }
                  isTimeExpired={isTimeExpired}
                />
              )}
              {params.page !== 'payment' && !isSmall && (
                <div className={classes.buttons}>
                  <Button
                    variant="outlined"
                    className={classes.btn}
                    onClick={handleStepDown}>
                    <Typography variant="h5" className={classes.cancelText}>
                      {params.page == 'cart'
                        ? getTranslate('Back To Booking')
                        : getTranslate('Back To Step 1')}
                    </Typography>
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    className={classes.btn}
                    disabled={isContinueDisabled(
                      params.page,
                      confirmPrimitiveInput,
                      cartData?.cartResponse.cart.items.length ?? 0,
                      isTimeExpired,
                      loadingForMakePrimitive,
                      loadingForConfirmPrimitiveData,
                      primitiveData
                    )}
                    onClick={handleStepUp}>
                    <Typography variant="h4" className={classes.continueText}>
                      {getTranslate('Continue')}
                    </Typography>
                  </Button>
                </div>
              )}
              {params.page === 'payment' && isSmall && (
                <div className={classes.buttons}>
                  <PaymentGateWay
                    totalAmount={
                      confirmPrimitiveReservationState?.reservationInfo
                        .totalAmount
                    }
                    paymentInfo={paymentInfo}
                    paymentMethod={paymentMethod}
                    reservationNumber={
                      confirmPrimitiveReservationState?.reservationNumber || 0
                    }
                    isTimeExpired={isTimeExpired}
                  />
                </div>
              )}
              {params.page !== 'payment' && isSmall && (
                <div className={classes.buttons}>
                  <Button
                    variant="outlined"
                    className={classes.btn}
                    onClick={handleStepDown}>
                    <Typography variant="h5" className={classes.cancelText}>
                      {params.page == 'cart'
                        ? getTranslate('Back To Booking')
                        : getTranslate('Back To Step 1')}
                    </Typography>
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    className={classes.btn}
                    disabled={isContinueDisabled(
                      params.page,
                      confirmPrimitiveInput,
                      cartData?.cartResponse.cart.items.length ?? 0,
                      isTimeExpired,
                      loadingForMakePrimitive,
                      loadingForConfirmPrimitiveData,
                      primitiveData
                    )}
                    onClick={handleStepUp}>
                    <Typography variant="h4" className={classes.continueText}>
                      {getTranslate('Continue')}
                    </Typography>
                  </Button>
                </div>
              )}
            </div>
          </>
        ) : (
          !loadingForGetCart &&
          !loadingForConfirmPrimitiveData &&
          !loadingForMakePrimitive && (
            <div className={classes.noResultFound}>
              <img src={noResultsFound} className={classes.img} />
              <Typography variant="h5" className={classes.noResults}>
                {getTranslate('No result Found')}
              </Typography>
            </div>
          )
        )}
      </div>
      <AdotelSkeleton
        open={
          (clickedItemHash === 'hash' &&
            (loadingForGetCart ||
              loadingForMakePrimitive ||
              loadingForConfirmPrimitiveData)) ||
          loadingForConfirmPrimitiveData ||
          loadingForMakePrimitive
        }
        circlesColor={theme.palette.primary.main}
        numberOfCircles={4}
      />
      <RedirectModal
        isOpen={showModal}
        title={redirectTitle}
        onClose={() => {
          setShowModal(false)
        }}
        onClick={() => {
          if (params.page === 'form') {
            history.push('/bookstepper/cart')
          } else {
            window.location.href = redirect || `/`
          }
        }}
      />
    </>
  )
}

export default BookingStepper
